import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pin, PinOff } from "lucide-react";
import { drw, errText } from "./api";

/** DRW-7 — Group: propose a grouping of every page in the set, show which rule placed each page,
 *  apply on confirm. Pinned pages are never moved by a re-run. */
export default function GroupDialog({ setId, onApplied, onClose }) {
  const [prop, setProp] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { drw.post(`/sets/${setId}/group-preview`).then(({ data }) => setProp(data)).catch((e) => toast.error(errText(e))); }, [setId]);
  const confirm = async () => {
    setBusy(true);
    try { await drw.post(`/sets/${setId}/group-apply`); toast.success("Pages grouped"); onApplied?.(); onClose(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-3" data-testid="group-dialog">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-3 border-b">
          <div>
            <div className="font-semibold">Group the pages of this set</div>
            <div className="text-xs text-neutral-500">A page follows its takeoff marks; a detail reached by a callout follows the plan that points at it; the rest go by discipline.</div>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="h-8 px-3 rounded-md border text-sm" data-testid="group-cancel">Cancel</button>
            <button type="button" onClick={confirm} disabled={!prop || busy} className="h-8 px-3 rounded-md bg-neutral-900 text-white text-sm disabled:opacity-50" data-testid="group-confirm">Confirm</button>
          </div>
        </div>
        <div className="overflow-auto p-3 space-y-3">
          {!prop && <div className="text-sm text-neutral-500">Working out the groups…</div>}
          {prop?.groups.map((g) => (
            <div key={g.name} data-testid={`group-preview-${g.name}`}>
              <div className="flex items-center gap-2 text-sm font-semibold"><span>{g.name}</span><span className="text-xs text-neutral-500 font-normal">{g.pages.length} page{g.pages.length === 1 ? "" : "s"}</span></div>
              <table className="w-full text-xs mt-1">
                <tbody>
                  {g.pages.map((p) => (
                    <tr key={p.sheet_id} className="border-t">
                      <td className="py-1 font-mono font-bold w-24">{p.sheet_number}</td>
                      <td className="py-1 truncate max-w-[18rem]">{p.title}</td>
                      <td className="py-1 text-neutral-500">{p.pinned ? <span className="inline-flex items-center gap-1"><Pin className="w-3 h-3" /> pinned</span> : p.rule}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Pin / unpin a page to a group — the manual override a re-run never touches. */
export function PinControl({ sheet, groups, onChanged }) {
  const pin = async (group) => {
    try { await drw.patch(`/sheets/${sheet.id}/group`, { group, pinned: true }); onChanged?.(); } catch (e) { toast.error(errText(e)); }
  };
  const unpin = async () => {
    try { await drw.patch(`/sheets/${sheet.id}/group`, { group: sheet.group || "", pinned: false }); onChanged?.(); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      {sheet.group_pinned
        ? <button type="button" onClick={unpin} title="Unpin — let Group place this page" className="p-0.5 rounded hover:bg-neutral-100 text-orange-700" data-testid={`unpin-${sheet.sheet_number}`}><PinOff className="w-3 h-3" /></button>
        : (
          <select value="" onChange={(e) => e.target.value && pin(e.target.value)} className="h-5 text-[10px] border rounded px-0.5 max-w-[5.5rem] opacity-0 group-hover:opacity-100" title="Pin this page to a group" data-testid={`pin-${sheet.sheet_number}`}>
            <option value="">pin to…</option>
            {groups.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        )}
    </span>
  );
}
